import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import apiService from '@/services/api'

export interface Notification {
  id: string
  user_id: string
  type: 'new_message' | 'file_uploaded' | 'file_note_added' | string
  title: string
  body: string
  link?: string
  read_at?: string
  metadata?: Record<string, any>
  created_at: string
  updated_at: string
}

export interface NotificationPreferences {
  email_enabled: boolean
  email_types: string[]
  in_app_enabled: boolean
  in_app_types: string[]
}

export interface Toast {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message?: string
  duration?: number
  action?: {
    label: string
    handler: () => void
  }
}

export const useNotificationsStore = defineStore('notifications', () => {
  const notifications = ref<Notification[]>([])
  const preferences = ref<NotificationPreferences | null>(null)
  const toasts = ref<Toast[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const unreadCount = ref(0)
  const hasMore = ref(true)
  const currentPage = ref(1)

  const unreadNotifications = computed(() => notifications.value.filter((n) => !n.read_at))

  const readNotifications = computed(() => notifications.value.filter((n) => n.read_at))

  async function loadNotifications(params?: {
    page?: number
    pageSize?: number
    unreadOnly?: boolean
  }): Promise<void> {
    loading.value = true
    error.value = null

    try {
      const response = await apiService.getNotifications(params)

      if (params?.page === 1 || !params?.page) {
        notifications.value = response.notifications
      } else {
        notifications.value.push(...response.notifications)
      }

      unreadCount.value = response.unread_count
      hasMore.value = response.has_more
      currentPage.value = params?.page || 1
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load notifications'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function markAsRead(notificationId: string): Promise<void> {
    try {
      await apiService.markNotificationRead(notificationId)

      const notification = notifications.value.find((n) => n.id === notificationId)
      if (notification && !notification.read_at) {
        notification.read_at = new Date().toISOString()
        unreadCount.value = Math.max(0, unreadCount.value - 1)
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to mark notification as read'
      throw err
    }
  }

  async function refreshUnreadCount(): Promise<void> {
    try {
      const response = await apiService.getUnreadNotificationCount()
      unreadCount.value = response.unread_count
    } catch (err) {
      // Silent failure - background sync
      console.warn('Failed to refresh unread count:', err)
    }
  }

  async function markAllAsRead(): Promise<void> {
    try {
      await apiService.markAllNotificationsRead()

      notifications.value.forEach((notification) => {
        if (!notification.read_at) {
          notification.read_at = new Date().toISOString()
        }
      })
      unreadCount.value = 0
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to mark all as read'
      throw err
    }
  }

  async function deleteNotification(notificationId: string): Promise<void> {
    try {
      await apiService.deleteNotification(notificationId)

      const index = notifications.value.findIndex((n) => n.id === notificationId)
      if (index !== -1) {
        const notification = notifications.value[index]
        if (!notification.read_at) {
          unreadCount.value = Math.max(0, unreadCount.value - 1)
        }
        notifications.value.splice(index, 1)
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete notification'
      throw err
    }
  }

  async function loadPreferences(): Promise<void> {
    try {
      const response = await apiService.getNotificationPreferences()
      preferences.value = response
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load preferences'
      throw err
    }
  }

  async function updatePreferences(newPreferences: NotificationPreferences): Promise<void> {
    try {
      const response = await apiService.updateNotificationPreferences(newPreferences)
      preferences.value = response
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update preferences'
      throw err
    }
  }

  function showToast(
    typeOrToast: 'success' | 'error' | 'warning' | 'info' | Omit<Toast, 'id'>,
    title?: string,
    message?: string
  ): void {
    let toast: Omit<Toast, 'id'>

    if (typeof typeOrToast === 'string') {
      toast = {
        type: typeOrToast,
        title: title || '',
        message
      }
    } else {
      toast = typeOrToast
    }

    const id = crypto.randomUUID()
    const duration = toast.duration || 5000

    const newToast: Toast = {
      ...toast,
      id,
      duration
    }

    toasts.value.push(newToast)

    if (toasts.value.length > 3) {
      toasts.value.shift()
    }

    if (duration > 0) {
      setTimeout(() => {
        removeToast(id)
      }, duration)
    }
  }

  function removeToast(id: string): void {
    const index = toasts.value.findIndex((t) => t.id === id)
    if (index !== -1) {
      toasts.value.splice(index, 1)
    }
  }

  function showSuccess(title: string, message?: string): void {
    showToast('success', title, message)
  }

  function showError(title: string, message?: string): void {
    showToast('error', title, message)
  }

  function showWarning(title: string, message?: string): void {
    showToast('warning', title, message)
  }

  function showInfo(title: string, message?: string): void {
    showToast('info', title, message)
  }

  function handleWebSocketNotification(notification: Notification): void {
    notifications.value.unshift(notification)
    unreadCount.value++

    showInfo(notification.title, notification.body)
  }

  async function loadMore(): Promise<void> {
    if (!hasMore.value || loading.value) return

    await loadNotifications({
      page: currentPage.value + 1,
      pageSize: 20
    })
  }

  function clearAll(): void {
    notifications.value = []
    unreadCount.value = 0
    hasMore.value = true
    currentPage.value = 1
  }

  return {
    notifications,
    preferences,
    toasts,
    loading,
    error,
    unreadCount,
    hasMore,
    currentPage,
    unreadNotifications,
    readNotifications,
    loadNotifications,
    markAsRead,
    refreshUnreadCount,
    markAllAsRead,
    deleteNotification,
    loadPreferences,
    updatePreferences,
    showToast,
    removeToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    handleWebSocketNotification,
    loadMore,
    clearAll
  }
})
